import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  Database,
  DollarSign,
  BarChart3,
  Calendar,
  Info,
  AlertCircle,
  Table,
  Eye
} from 'lucide-react';
import { getRevenueTypes, getRoomStatTypes } from '../../utils/api';

// Helper function to safely convert header values to strings
const safeHeaderValue = (header, index) => {
  if (header === null || header === undefined) {
    return `Col ${index + 1}`;
  }
  const stringValue = typeof header === 'string' ? header : String(header);
  const trimmed = stringValue.trim();
  return trimmed || `Col ${index + 1}`;
};

const HistoricalRevenueMapping = ({
  extractedData,
  schemaConfig,
  onMappingChange,
  onNext,
  onBack,
  selectedLocation,
  hideFooter = false
}) => {
  // State for historical mappings
  const [historicalConfig, setHistoricalConfig] = useState({
    // Revenue mappings
    revenueMappings: [],
    // Room stat mappings
    roomStatMappings: [],
    // Date configuration
    dateColumn: null,
    dateFormat: 'MM/DD/YYYY',
    // Source table selection mode: 'single', 'multiple', or 'all'
    tableSelectionMode: 'all',
    // Source table (for single mode)
    sourceTable: 0,
    // Selected tables (for multiple mode) - array of table indices
    selectedTables: [],
    // Row range
    rowRange: { type: 'all', start: null, end: null },
    // Header row (0-indexed)
    headerRow: 0,
    // Data starts at row (0-indexed, after header)
    dataStartRow: 1
  });

  // State for available types
  const [revenueTypes, setRevenueTypes] = useState([]);
  const [roomStatTypes, setRoomStatTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTable, setSelectedTable] = useState(0);
  const [activeTab, setActiveTab] = useState('revenue'); // 'revenue' or 'roomStats'
  const [showTablePreview, setShowTablePreview] = useState(true); // Table preview expanded state

  // Ref to track if initial config has been loaded
  const initializedRef = useRef(false);

  // Load revenue types and room stat types
  useEffect(() => {
    const loadTypes = async () => {
      try {
        setLoading(true);
        setError(null);

        const orgId = localStorage.getItem('currentOrgId');
        const locationId = selectedLocation?.id;

        if (!orgId) {
          setError('No organization selected');
          return;
        }

        const [revTypes, statTypes] = await Promise.all([
          getRevenueTypes(orgId, locationId),
          getRoomStatTypes(orgId, locationId)
        ]);

        setRevenueTypes(revTypes || []);
        setRoomStatTypes(statTypes || []);
      } catch (err) {
        console.error('Error loading types:', err);
        setError('Failed to load revenue and room stat types');
      } finally {
        setLoading(false);
      }
    };

    loadTypes();
  }, [selectedLocation]);

  // Load existing config from schemaConfig (only once on initial mount)
  useEffect(() => {
    if (!initializedRef.current && schemaConfig?.fieldMappings?.historical) {
      initializedRef.current = true;
      setHistoricalConfig(prev => ({
        ...prev,
        ...schemaConfig.fieldMappings.historical
      }));
    }
  }, []); // Empty dependency - only run on mount

  // Propagate changes to parent (debounced to prevent rapid updates)
  const prevHistoricalConfigRef = useRef(null);
  useEffect(() => {
    // Skip the initial mount or if config hasn't actually changed
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }

    // Only propagate if the config has actually changed
    const configStr = JSON.stringify(historicalConfig);
    if (prevHistoricalConfigRef.current !== configStr) {
      prevHistoricalConfigRef.current = configStr;
      onMappingChange({
        historical: historicalConfig
      });
    }
  }, [historicalConfig, onMappingChange]);

  // Get available columns from extracted data
  const getAvailableColumns = useCallback(() => {
    if (!extractedData?.tables || extractedData.tables.length === 0) {
      return [];
    }

    const table = extractedData.tables[selectedTable];
    if (!table) return [];

    // Try to get headers from different sources
    let headers = [];
    if (table.headers && Array.isArray(table.headers)) {
      headers = table.headers;
    } else if (table.data && table.data.length > 0) {
      // Use first row as headers
      headers = table.data[historicalConfig.headerRow] || table.data[0] || [];
    }

    return headers.map((header, index) => ({
      index,
      name: safeHeaderValue(header, index),
      sampleValues: getSampleValues(table, index)
    }));
  }, [extractedData, selectedTable, historicalConfig.headerRow]);

  // Get sample values for a column
  const getSampleValues = (table, colIndex) => {
    if (!table?.data) return [];
    const startRow = historicalConfig.dataStartRow || 1;
    return table.data
      .slice(startRow, startRow + 3)
      .map(row => row[colIndex])
      .filter(val => val !== null && val !== undefined)
      .map(val => String(val).substring(0, 30));
  };

  // Update historical config
  const updateConfig = (updates) => {
    setHistoricalConfig(prev => ({ ...prev, ...updates }));
  };

  // Add a revenue mapping
  const addRevenueMapping = () => {
    setHistoricalConfig(prev => ({
      ...prev,
      revenueMappings: [
        ...prev.revenueMappings,
        {
          id: Date.now(),
          revenueTypeId: null,
          valueColumn: null,
          valueType: 'currentValue', // currentValue, mtd, ytd, lytd, lymtd, ly
          // Multi-column support
          useMultiColumn: false,
          columns: [], // Array of { columnIndex, operator } - operator applies BEFORE this column's value
          // First column has no operator, subsequent columns have: 'add', 'subtract', 'multiply', 'divide'
        }
      ]
    }));
  };

  // Add a column to a revenue mapping's multi-column configuration
  const addColumnToRevenueMapping = (mappingId) => {
    setHistoricalConfig(prev => ({
      ...prev,
      revenueMappings: prev.revenueMappings.map(mapping => {
        if (mapping.id !== mappingId) return mapping;
        const newColumns = [...(mapping.columns || [])];
        newColumns.push({
          columnIndex: null,
          operator: newColumns.length === 0 ? null : 'add' // First column has no operator
        });
        return { ...mapping, columns: newColumns };
      })
    }));
  };

  // Update a column in a revenue mapping's multi-column configuration
  const updateColumnInRevenueMapping = (mappingId, colIdx, field, value) => {
    setHistoricalConfig(prev => ({
      ...prev,
      revenueMappings: prev.revenueMappings.map(mapping => {
        if (mapping.id !== mappingId) return mapping;
        const newColumns = [...(mapping.columns || [])];
        newColumns[colIdx] = { ...newColumns[colIdx], [field]: value };
        return { ...mapping, columns: newColumns };
      })
    }));
  };

  // Remove a column from a revenue mapping's multi-column configuration
  const removeColumnFromRevenueMapping = (mappingId, colIdx) => {
    setHistoricalConfig(prev => ({
      ...prev,
      revenueMappings: prev.revenueMappings.map(mapping => {
        if (mapping.id !== mappingId) return mapping;
        const newColumns = (mapping.columns || []).filter((_, i) => i !== colIdx);
        // If removing first column, clear operator from new first column
        if (colIdx === 0 && newColumns.length > 0) {
          newColumns[0] = { ...newColumns[0], operator: null };
        }
        return { ...mapping, columns: newColumns };
      })
    }));
  };

  // Update a revenue mapping
  const updateRevenueMapping = (id, field, value) => {
    setHistoricalConfig(prev => ({
      ...prev,
      revenueMappings: prev.revenueMappings.map(mapping =>
        mapping.id === id ? { ...mapping, [field]: value } : mapping
      )
    }));
  };

  // Remove a revenue mapping
  const removeRevenueMapping = (id) => {
    setHistoricalConfig(prev => ({
      ...prev,
      revenueMappings: prev.revenueMappings.filter(mapping => mapping.id !== id)
    }));
  };

  // Add a room stat mapping
  const addRoomStatMapping = () => {
    setHistoricalConfig(prev => ({
      ...prev,
      roomStatMappings: [
        ...prev.roomStatMappings,
        {
          id: Date.now(),
          roomStatTypeId: null,
          valueColumn: null,
          valueType: 'currentValue', // currentValue, mtd, ytd, lytd, lymtd, ly
          // Multi-column support
          useMultiColumn: false,
          columns: [], // Array of { columnIndex, operator }
        }
      ]
    }));
  };

  // Add a column to a room stat mapping's multi-column configuration
  const addColumnToRoomStatMapping = (mappingId) => {
    setHistoricalConfig(prev => ({
      ...prev,
      roomStatMappings: prev.roomStatMappings.map(mapping => {
        if (mapping.id !== mappingId) return mapping;
        const newColumns = [...(mapping.columns || [])];
        newColumns.push({
          columnIndex: null,
          operator: newColumns.length === 0 ? null : 'add'
        });
        return { ...mapping, columns: newColumns };
      })
    }));
  };

  // Update a column in a room stat mapping's multi-column configuration
  const updateColumnInRoomStatMapping = (mappingId, colIdx, field, value) => {
    setHistoricalConfig(prev => ({
      ...prev,
      roomStatMappings: prev.roomStatMappings.map(mapping => {
        if (mapping.id !== mappingId) return mapping;
        const newColumns = [...(mapping.columns || [])];
        newColumns[colIdx] = { ...newColumns[colIdx], [field]: value };
        return { ...mapping, columns: newColumns };
      })
    }));
  };

  // Remove a column from a room stat mapping's multi-column configuration
  const removeColumnFromRoomStatMapping = (mappingId, colIdx) => {
    setHistoricalConfig(prev => ({
      ...prev,
      roomStatMappings: prev.roomStatMappings.map(mapping => {
        if (mapping.id !== mappingId) return mapping;
        const newColumns = (mapping.columns || []).filter((_, i) => i !== colIdx);
        if (colIdx === 0 && newColumns.length > 0) {
          newColumns[0] = { ...newColumns[0], operator: null };
        }
        return { ...mapping, columns: newColumns };
      })
    }));
  };

  // Update a room stat mapping
  const updateRoomStatMapping = (id, field, value) => {
    setHistoricalConfig(prev => ({
      ...prev,
      roomStatMappings: prev.roomStatMappings.map(mapping =>
        mapping.id === id ? { ...mapping, [field]: value } : mapping
      )
    }));
  };

  // Remove a room stat mapping
  const removeRoomStatMapping = (id) => {
    setHistoricalConfig(prev => ({
      ...prev,
      roomStatMappings: prev.roomStatMappings.filter(mapping => mapping.id !== id)
    }));
  };

  const availableColumns = getAvailableColumns();

  // Get table preview data
  const getTablePreviewData = useCallback(() => {
    if (!extractedData?.tables || extractedData.tables.length === 0) {
      return { headers: [], rows: [], totalRows: 0 };
    }

    const table = extractedData.tables[selectedTable];
    if (!table?.data) return { headers: [], rows: [], totalRows: 0 };

    const headerRowIndex = historicalConfig.headerRow || 0;
    const dataStartRowIndex = historicalConfig.dataStartRow || 1;

    // Get headers
    const headers = (table.data[headerRowIndex] || []).map((header, index) =>
      safeHeaderValue(header, index)
    );

    // Get preview rows (show up to 10 rows)
    const previewRowCount = 10;
    const rows = table.data
      .slice(dataStartRowIndex, dataStartRowIndex + previewRowCount)
      .map(row => row.map(cell => (cell === null || cell === undefined ? '' : String(cell))));

    return {
      headers,
      rows,
      totalRows: Math.max(0, table.data.length - dataStartRowIndex),
      headerRowIndex,
      dataStartRowIndex
    };
  }, [extractedData, selectedTable, historicalConfig.headerRow, historicalConfig.dataStartRow]);

  const tablePreviewData = getTablePreviewData();

  const valueTypeOptions = [
    { value: 'currentValue', label: 'currentValue (Current/Daily)' },
    { value: 'mtd', label: 'mtd (Month-to-Date)' },
    { value: 'ytd', label: 'ytd (Year-to-Date)' },
    { value: 'lytd', label: 'lytd (Last Year-to-Date)' },
    { value: 'lymtd', label: 'lymtd (Last Year MTD)' },
    { value: 'ly', label: 'ly (Last Year Same Day)' },
    { value: 'roomsCurrentValue', label: 'roomsCurrentValue (Rooms Current/Daily)' },
    { value: 'roomsMtd', label: 'roomsMtd (Rooms Month-to-Date)' },
    { value: 'roomsYtd', label: 'roomsYtd (Rooms Year-to-Date)' },
    { value: 'roomsLytd', label: 'roomsLytd (Rooms Last Year-to-Date)' },
    { value: 'roomsLymtd', label: 'roomsLymtd (Rooms Last Year MTD)' },
    { value: 'roomsLy', label: 'roomsLy (Rooms Last Year Same Day)' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/15/2024)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (15/01/2024)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-01-15)' },
    { value: 'M/D/YYYY', label: 'M/D/YYYY (1/15/2024)' },
    { value: 'M/D/YY', label: 'M/D/YY (1/15/24 or 10/1/25)' },
    { value: 'M/D/YY DOW', label: 'M/D/YY + Day (02/01/25 Sat)' },
    { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Jan 15, 2024)' },
    { value: 'MMMM DD, YYYY', label: 'MMMM DD, YYYY (January 15, 2024)' }
  ];

  const operatorOptions = [
    { value: 'add', label: '+', description: 'Add' },
    { value: 'subtract', label: '−', description: 'Subtract' },
    { value: 'multiply', label: '×', description: 'Multiply' },
    { value: 'divide', label: '÷', description: 'Divide' }
  ];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading configuration options...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Historical Data Mapping
        </h3>
        <p className="cp-help">
          Map columns from your historical PDF to revenue and room statistics data fields.
          This data will be imported into the hotelRevenue and hotelRoomStatsNormalized tables.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 dark:text-red-400">{error}</span>
          </div>
        </div>
      )}

      {/* Source Table Selection */}
      {extractedData?.tables && extractedData.tables.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Database className="w-5 h-5 mr-2 text-blue-600" />
            Table Selection
          </h4>

          {/* Selection Mode */}
          <div className="mb-4">
            <label className="cp-label">
              Selection Mode
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="tableSelectionMode"
                  value="all"
                  checked={historicalConfig.tableSelectionMode === 'all'}
                  onChange={() => updateConfig({ tableSelectionMode: 'all', selectedTables: [] })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">All Tables</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="tableSelectionMode"
                  value="single"
                  checked={historicalConfig.tableSelectionMode === 'single'}
                  onChange={() => updateConfig({ tableSelectionMode: 'single', selectedTables: [] })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Single Table</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="tableSelectionMode"
                  value="multiple"
                  checked={historicalConfig.tableSelectionMode === 'multiple'}
                  onChange={() => updateConfig({ tableSelectionMode: 'multiple', selectedTables: [] })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Select Multiple</span>
              </label>
            </div>
          </div>

          {/* Single Table Selector */}
          {historicalConfig.tableSelectionMode === 'single' && (
            <div>
              <label className="cp-label">
                Select Table
              </label>
              <select
                value={selectedTable}
                onChange={(e) => {
                  const tableIndex = parseInt(e.target.value);
                  setSelectedTable(tableIndex);
                  updateConfig({ sourceTable: tableIndex });
                }}
                className="cp-input"
              >
                {extractedData.tables.map((table, index) => (
                  <option key={index} value={index}>
                    Table {index + 1} ({table.data?.length || 0} rows)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Multiple Table Selector */}
          {historicalConfig.tableSelectionMode === 'multiple' && (
            <div>
              <label className="cp-label">
                Select Tables to Include
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {extractedData.tables.map((table, index) => (
                  <label key={index} className="flex items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                    <input
                      type="checkbox"
                      checked={historicalConfig.selectedTables?.includes(index) || false}
                      onChange={(e) => {
                        const currentSelected = historicalConfig.selectedTables || [];
                        const newSelected = e.target.checked
                          ? [...currentSelected, index]
                          : currentSelected.filter(i => i !== index);
                        updateConfig({ selectedTables: newSelected.sort((a, b) => a - b) });
                        // Update preview table to first selected
                        if (newSelected.length > 0) {
                          setSelectedTable(newSelected[0]);
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Table {index + 1} ({table.data?.length || 0} rows)
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* All Tables Info */}
          {historicalConfig.tableSelectionMode === 'all' && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              All {extractedData.tables.length} tables will be processed (total {extractedData.tables.reduce((sum, t) => sum + (t.data?.length || 0), 0)} rows)
            </div>
          )}

          {/* Preview Table Selector (for column mapping reference) */}
          {(historicalConfig.tableSelectionMode === 'all' || historicalConfig.tableSelectionMode === 'multiple') && (
            <div className="mt-4">
              <label className="cp-label">
                Preview Table (for column reference)
              </label>
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(parseInt(e.target.value))}
                className="cp-input"
              >
                {extractedData.tables.map((table, index) => (
                  <option key={index} value={index}>
                    Table {index + 1} ({table.data?.length || 0} rows)
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select which table to use for column name reference when setting up mappings
              </p>
            </div>
          )}
        </div>
      )}

      {/* Table Preview Section */}
      {extractedData?.tables && extractedData.tables.length > 0 && (
        <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {/* Preview Header - Collapsible */}
          <button
            onClick={() => setShowTablePreview(!showTablePreview)}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
          >
            <div className="flex items-center">
              <Eye className="w-5 h-5 mr-2 text-blue-600" />
              <span className="font-medium text-gray-900 dark:text-white">Table Preview</span>
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                (Table {selectedTable + 1} - {tablePreviewData.totalRows} data rows)
              </span>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform ${showTablePreview ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Preview Content */}
          {showTablePreview && (
            <div className="p-4 bg-white dark:bg-gray-900">
              {tablePreviewData.headers.length > 0 ? (
                <div className="overflow-auto max-h-[70vh]">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead>
                      <tr className="bg-blue-50 dark:bg-blue-900/30">
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-700">
                          Row
                        </th>
                        {tablePreviewData.headers.map((header, colIndex) => (
                          <th
                            key={colIndex}
                            className="px-3 py-2 text-left text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                          >
                            <div className="flex flex-col">
                              <span className="text-[10px] text-gray-400 dark:text-gray-500">Col {colIndex}</span>
                              <span className="truncate max-w-[120px]" title={header}>{header}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {tablePreviewData.rows.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'}
                        >
                          <td className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 border-r border-gray-200 dark:border-gray-700 font-mono">
                            {tablePreviewData.dataStartRowIndex + rowIndex}
                          </td>
                          {row.map((cell, colIndex) => (
                            <td
                              key={colIndex}
                              className="px-3 py-2 text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                            >
                              <span className="truncate block max-w-[120px]" title={cell}>
                                {cell || <span className="text-gray-300 dark:text-gray-600 italic">empty</span>}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {tablePreviewData.totalRows > 10 && (
                    <div className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                      Showing first 10 of {tablePreviewData.totalRows} data rows
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  <Table className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No data available in this table</p>
                </div>
              )}

              {/* Legend */}
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 mr-1.5"></div>
                  <span>Header row (row {historicalConfig.headerRow})</span>
                </div>
                <div className="flex items-center">
                  <span className="font-mono mr-1.5">Col #</span>
                  <span>Column index for mapping</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Row Configuration */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="cp-label">
            Header Row (0-indexed)
          </label>
          <input
            type="number"
            min="0"
            value={historicalConfig.headerRow}
            onChange={(e) => updateConfig({ headerRow: parseInt(e.target.value) || 0 })}
            className="cp-input"
          />
        </div>
        <div>
          <label className="cp-label">
            Data Start Row (0-indexed)
          </label>
          <input
            type="number"
            min="0"
            value={historicalConfig.dataStartRow}
            onChange={(e) => updateConfig({ dataStartRow: parseInt(e.target.value) || 1 })}
            className="cp-input"
          />
        </div>
        <div>
          <label className="cp-label">
            Row Range
          </label>
          <select
            value={historicalConfig.rowRange.type}
            onChange={(e) => updateConfig({ rowRange: { ...historicalConfig.rowRange, type: e.target.value } })}
            className="cp-input"
          >
            <option value="all">All Rows</option>
            <option value="range">Specific Range</option>
          </select>
        </div>
      </div>

      {historicalConfig.rowRange.type === 'range' && (
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div>
            <label className="cp-label">
              Start Row
            </label>
            <input
              type="number"
              min="0"
              value={historicalConfig.rowRange.start || ''}
              onChange={(e) => updateConfig({
                rowRange: { ...historicalConfig.rowRange, start: parseInt(e.target.value) || null }
              })}
              className="cp-input"
            />
          </div>
          <div>
            <label className="cp-label">
              End Row
            </label>
            <input
              type="number"
              min="0"
              value={historicalConfig.rowRange.end || ''}
              onChange={(e) => updateConfig({
                rowRange: { ...historicalConfig.rowRange, end: parseInt(e.target.value) || null }
              })}
              className="cp-input"
            />
          </div>
        </div>
      )}

      {/* Date Configuration */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-blue-600" />
          Date Configuration
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="cp-label">
              Date Column
            </label>
            <select
              value={historicalConfig.dateColumn ?? ''}
              onChange={(e) => updateConfig({ dateColumn: e.target.value ? parseInt(e.target.value) : null })}
              className="cp-input"
            >
              <option value="">Select a column...</option>
              {availableColumns.map((col) => (
                <option key={col.index} value={col.index}>
                  {col.name} {col.sampleValues.length > 0 && `(e.g., ${col.sampleValues[0]})`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="cp-label">
              Date Format
            </label>
            <select
              value={historicalConfig.dateFormat}
              onChange={(e) => updateConfig({ dateFormat: e.target.value })}
              className="cp-input"
            >
              {dateFormatOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('revenue')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'revenue'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DollarSign className="inline w-4 h-4 mr-1" />
            Revenue Mappings ({historicalConfig.revenueMappings.length})
          </button>
          <button
            onClick={() => setActiveTab('roomStats')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roomStats'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BarChart3 className="inline w-4 h-4 mr-1" />
            Room Stats Mappings ({historicalConfig.roomStatMappings.length})
          </button>
        </nav>
      </div>

      {/* Revenue Mappings Tab */}
      {activeTab === 'revenue' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Map columns to revenue types. Data will be inserted into the hotelRevenue table.
            </p>
            <button
              onClick={addRevenueMapping}
              className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Mapping
            </button>
          </div>

          {historicalConfig.revenueMappings.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No revenue mappings configured</p>
              <p className="text-sm">Click "Add Mapping" to start mapping columns to revenue types</p>
            </div>
          ) : (
            <div className="space-y-3">
              {historicalConfig.revenueMappings.map((mapping, index) => (
                <div
                  key={mapping.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="cp-label">
                      Revenue Mapping #{index + 1}
                    </span>
                    <button
                      onClick={() => removeRevenueMapping(mapping.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="cp-label">
                        Revenue Type
                      </label>
                      <select
                        value={mapping.revenueTypeId || ''}
                        onChange={(e) => updateRevenueMapping(mapping.id, 'revenueTypeId', e.target.value ? parseInt(e.target.value) : null)}
                        className="cp-input"
                      >
                        <option value="">Select revenue type...</option>
                        {revenueTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="cp-label">
                        Value Type
                      </label>
                      <select
                        value={mapping.valueType}
                        onChange={(e) => updateRevenueMapping(mapping.id, 'valueType', e.target.value)}
                        className="cp-input"
                      >
                        {valueTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Multi-column toggle */}
                  <div className="flex items-center mb-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={mapping.useMultiColumn || false}
                        onChange={(e) => {
                          updateRevenueMapping(mapping.id, 'useMultiColumn', e.target.checked);
                          // Initialize columns array if enabling multi-column
                          if (e.target.checked && (!mapping.columns || mapping.columns.length === 0)) {
                            updateRevenueMapping(mapping.id, 'columns', [{ columnIndex: mapping.valueColumn, operator: null }]);
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Combine multiple columns (use operators)
                      </span>
                    </label>
                  </div>

                  {/* Single column mode */}
                  {!mapping.useMultiColumn && (
                    <div>
                      <label className="cp-label">
                        Source Column
                      </label>
                      <select
                        value={mapping.valueColumn ?? ''}
                        onChange={(e) => updateRevenueMapping(mapping.id, 'valueColumn', e.target.value ? parseInt(e.target.value) : null)}
                        className="cp-input"
                      >
                        <option value="">Select column...</option>
                        {availableColumns.map((col) => (
                          <option key={col.index} value={col.index}>
                            {col.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Multi-column mode */}
                  {mapping.useMultiColumn && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded p-3 bg-gray-50 dark:bg-gray-900">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                          Column Formula
                        </label>
                        <button
                          onClick={() => addColumnToRevenueMapping(mapping.id)}
                          className="flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Column
                        </button>
                      </div>

                      {(!mapping.columns || mapping.columns.length === 0) ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                          Click "Add Column" to add columns to the formula
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {mapping.columns.map((col, colIdx) => (
                            <div key={colIdx} className="flex items-center gap-2">
                              {/* Operator (not for first column) */}
                              {colIdx > 0 && (
                                <select
                                  value={col.operator || 'add'}
                                  onChange={(e) => updateColumnInRevenueMapping(mapping.id, colIdx, 'operator', e.target.value)}
                                  className="w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-bold"
                                >
                                  {operatorOptions.map((op) => (
                                    <option key={op.value} value={op.value}>
                                      {op.label}
                                    </option>
                                  ))}
                                </select>
                              )}
                              {colIdx === 0 && <div className="w-16" />}

                              {/* Column selector */}
                              <select
                                value={col.columnIndex ?? ''}
                                onChange={(e) => updateColumnInRevenueMapping(mapping.id, colIdx, 'columnIndex', e.target.value ? parseInt(e.target.value) : null)}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                <option value="">Select column...</option>
                                {availableColumns.map((c) => (
                                  <option key={c.index} value={c.index}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>

                              {/* Remove column button */}
                              <button
                                onClick={() => removeColumnFromRevenueMapping(mapping.id, colIdx)}
                                className="p-1 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}

                          {/* Formula preview */}
                          {mapping.columns.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                              <span className="cp-help">Formula: </span>
                              <span className="text-xs font-mono text-gray-700 dark:text-gray-300">
                                {mapping.columns.map((col, colIdx) => {
                                  const colName = col.columnIndex !== null
                                    ? (availableColumns[col.columnIndex]?.name || `Col ${col.columnIndex}`)
                                    : '?';
                                  const opSymbol = colIdx > 0
                                    ? ` ${operatorOptions.find(o => o.value === col.operator)?.label || '+'} `
                                    : '';
                                  return `${opSymbol}[${colName}]`;
                                }).join('')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Room Stats Mappings Tab */}
      {activeTab === 'roomStats' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Map columns to room stat types. Data will be inserted into the hotelRoomStatsNormalized table.
            </p>
            <button
              onClick={addRoomStatMapping}
              className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Mapping
            </button>
          </div>

          {historicalConfig.roomStatMappings.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No room stat mappings configured</p>
              <p className="text-sm">Click "Add Mapping" to start mapping columns to room stat types</p>
            </div>
          ) : (
            <div className="space-y-3">
              {historicalConfig.roomStatMappings.map((mapping, index) => (
                <div
                  key={mapping.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="cp-label">
                      Room Stat Mapping #{index + 1}
                    </span>
                    <button
                      onClick={() => removeRoomStatMapping(mapping.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="cp-label">
                        Room Stat Type
                      </label>
                      <select
                        value={mapping.roomStatTypeId || ''}
                        onChange={(e) => updateRoomStatMapping(mapping.id, 'roomStatTypeId', e.target.value ? parseInt(e.target.value) : null)}
                        className="cp-input"
                      >
                        <option value="">Select room stat type...</option>
                        {roomStatTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="cp-label">
                        Value Type
                      </label>
                      <select
                        value={mapping.valueType}
                        onChange={(e) => updateRoomStatMapping(mapping.id, 'valueType', e.target.value)}
                        className="cp-input"
                      >
                        {valueTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Multi-column toggle */}
                  <div className="flex items-center mb-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={mapping.useMultiColumn || false}
                        onChange={(e) => {
                          updateRoomStatMapping(mapping.id, 'useMultiColumn', e.target.checked);
                          if (e.target.checked && (!mapping.columns || mapping.columns.length === 0)) {
                            updateRoomStatMapping(mapping.id, 'columns', [{ columnIndex: mapping.valueColumn, operator: null }]);
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Combine multiple columns (use operators)
                      </span>
                    </label>
                  </div>

                  {/* Single column mode */}
                  {!mapping.useMultiColumn && (
                    <div>
                      <label className="cp-label">
                        Source Column
                      </label>
                      <select
                        value={mapping.valueColumn ?? ''}
                        onChange={(e) => updateRoomStatMapping(mapping.id, 'valueColumn', e.target.value ? parseInt(e.target.value) : null)}
                        className="cp-input"
                      >
                        <option value="">Select column...</option>
                        {availableColumns.map((col) => (
                          <option key={col.index} value={col.index}>
                            {col.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Multi-column mode */}
                  {mapping.useMultiColumn && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded p-3 bg-gray-50 dark:bg-gray-900">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                          Column Formula
                        </label>
                        <button
                          onClick={() => addColumnToRoomStatMapping(mapping.id)}
                          className="flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Column
                        </button>
                      </div>

                      {(!mapping.columns || mapping.columns.length === 0) ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                          Click "Add Column" to add columns to the formula
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {mapping.columns.map((col, colIdx) => (
                            <div key={colIdx} className="flex items-center gap-2">
                              {colIdx > 0 && (
                                <select
                                  value={col.operator || 'add'}
                                  onChange={(e) => updateColumnInRoomStatMapping(mapping.id, colIdx, 'operator', e.target.value)}
                                  className="w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-bold"
                                >
                                  {operatorOptions.map((op) => (
                                    <option key={op.value} value={op.value}>
                                      {op.label}
                                    </option>
                                  ))}
                                </select>
                              )}
                              {colIdx === 0 && <div className="w-16" />}

                              <select
                                value={col.columnIndex ?? ''}
                                onChange={(e) => updateColumnInRoomStatMapping(mapping.id, colIdx, 'columnIndex', e.target.value ? parseInt(e.target.value) : null)}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                <option value="">Select column...</option>
                                {availableColumns.map((c) => (
                                  <option key={c.index} value={c.index}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>

                              <button
                                onClick={() => removeColumnFromRoomStatMapping(mapping.id, colIdx)}
                                className="p-1 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}

                          {mapping.columns.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                              <span className="cp-help">Formula: </span>
                              <span className="text-xs font-mono text-gray-700 dark:text-gray-300">
                                {mapping.columns.map((col, colIdx) => {
                                  const colName = col.columnIndex !== null
                                    ? (availableColumns[col.columnIndex]?.name || `Col ${col.columnIndex}`)
                                    : '?';
                                  const opSymbol = colIdx > 0
                                    ? ` ${operatorOptions.find(o => o.value === col.operator)?.label || '+'} `
                                    : '';
                                  return `${opSymbol}[${colName}]`;
                                }).join('')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
              Historical Data Import
            </h4>
            <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
              <li>Revenue data will be stored in the <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">hotelRevenue</code> table</li>
              <li>Room statistics will be stored in the <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">hotelRoomStatsNormalized</code> table</li>
              <li>Each row in your data will create records for each mapped type</li>
              <li>The date column will be used as the effective date for all records</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Configuration Summary */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Configuration Summary
        </h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="cp-help">Revenue Mappings:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">
              {historicalConfig.revenueMappings.length}
            </span>
          </div>
          <div>
            <span className="cp-help">Room Stat Mappings:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">
              {historicalConfig.roomStatMappings.length}
            </span>
          </div>
          <div>
            <span className="cp-help">Date Column:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">
              {historicalConfig.dateColumn !== null
                ? availableColumns[historicalConfig.dateColumn]?.name || `Column ${historicalConfig.dateColumn}`
                : 'Not selected'}
            </span>
          </div>
          <div>
            <span className="cp-help">Date Format:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">
              {historicalConfig.dateFormat}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation — hidden when the wizard frame owns it. */}
      <div className={`justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 ${hideFooter ? 'hidden' : 'flex'}`}>
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600
                   text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Preview
        </button>
        <button
          onClick={onNext}
          disabled={historicalConfig.revenueMappings.length === 0 && historicalConfig.roomStatMappings.length === 0}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                   disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue to Save
          <ChevronRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default HistoricalRevenueMapping;
